import { useEffect, useRef, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";

// Pages
import HomePage from "@/pages/HomePage";
import AboutPage from "@/pages/AboutPage";
import CRMLandingPage from "@/pages/CRMLandingPage";
import {
  CommerceLanding, CommerceServicePage, CommerceAllServices, CommercePricing,
  CommerceCaseStudies, CommerceBookConsultation, CommerceContact,
  CommerceInsights, CommerceInsightDetail,
  PrivacyPage, CookiesPage, TermsPage, ConsultancyTermsPage, RefundPage,
} from "@/pages/CommercePages";
import PulseLanding from "@/pages/pulse/PulseLanding";
import PulsePrivacyPolicy from "@/pages/pulse/PulsePrivacyPolicy";
import PulseTerms from "@/pages/pulse/PulseTerms";
import PulseDeleteAccount from "@/pages/pulse/PulseDeleteAccount";
import PulseUnsubscribe from "@/pages/pulse/PulseUnsubscribe";
import { CommercePlaybook, CommerceProposalView, CommerceAgreementSign, CommercePayInvoice } from "@/pages/CommerceClientPages";
import {
  CRMLoginPage,
  CRMRegisterChoicePage,
  CRMRegisterPersonalPage,
  CRMRegisterBusinessPage,
  CRMInviteAcceptPage,
} from "@/pages/CRMAuthPages";
import { CRMAuthProvider } from "@/contexts/CRMAuthContext";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import AuthCallback from "@/pages/AuthCallback";
import CustomerDashboard from "@/pages/CustomerDashboard";
import MerchantDashboard from "@/pages/MerchantDashboard";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminSEOPage from "@/pages/admin/AdminSEOPage";
import StorefrontPage from "@/pages/StorefrontPage";
import MerchantStorePage from "@/pages/MerchantStorePage";
import ProductDetailPage from "@/pages/ProductDetailPage";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import MerchantPricingPage from "@/pages/MerchantPricingPage";
import MerchantPaymentSuccess from "@/pages/MerchantPaymentSuccess";
import MerchantPaymentCancel from "@/pages/MerchantPaymentCancel";
import MerchantPayoutsPage from "@/pages/MerchantPayoutsPage";
import AgreementSignPage from "@/pages/AgreementSignPage";
import MerchantDocumentsPage from "@/pages/MerchantDocumentsPage";
import MerchantBillingPage from "@/pages/MerchantBillingPage";
import MerchantInvoicesPage from "@/pages/MerchantInvoicesPage";
import MerchantSubscriptionManage from "@/pages/MerchantSubscriptionManage";
import StoreSettingsPage from "@/pages/merchant/StoreSettingsPage";
import AIStudioPage from "@/pages/AIStudioPage";

// POS Pages
import POSProductsPage from "@/pages/pos/POSProductsPage";
import POSTerminalPage from "@/pages/pos/POSTerminalPage";
import POSPaymentSuccessPage from "@/pages/pos/POSPaymentSuccessPage";
import POSReceiptPage from "@/pages/pos/POSReceiptPage";
import POSStaffPage from "@/pages/pos/POSStaffPage";
import POSCustomersPage from "@/pages/pos/POSCustomersPage";
import POSReportsPage from "@/pages/pos/POSReportsPage";
import POSAIAssistantPage from "@/pages/pos/POSAIAssistantPage";
import POSRFIDAnalyticsPage from "@/pages/pos/POSRFIDAnalyticsPage";
import POSPromotionsPage from "@/pages/pos/POSPromotionsPage";
import POSSoftPOSPage from "@/pages/pos/POSSoftPOSPage";
import PosDemoPage from "@/pages/pos/PosDemoPage";
import POSClockPage from "@/pages/pos/POSClockPage";
import POSFeedbackPage from "@/pages/pos/POSFeedbackPage";

// Service Management Pages
import ServicesPage from "@/pages/services/ServicesPage";
import BookingsPage from "@/pages/services/BookingsPage";
import PublicBookingPage from "@/pages/services/PublicBookingPage";
import StaffBookingsPage from "@/pages/services/StaffBookingsPage";
import ServiceDemoPage from "@/pages/services/ServiceDemoPage";

// Feedback
import FeedbackPage from "@/pages/FeedbackPage";
import AdminFeedbackPage from "@/pages/admin/AdminFeedbackPage";

// AI Social
import AISocialPage from "@/pages/AISocialPage";
import AdminAICreditsPage from "@/pages/admin/AdminAICreditsPage";

// Merchant AI Credits Purchase
import MerchantAICreditsPage from "@/pages/merchant/AICreditsPage";

// CRM
import CRMPage from "@/pages/merchant/CRMPage";
import CRMAppPage from "@/pages/CRMAppPage";
import CRMOnboardingWizard from "@/pages/CRMOnboardingWizard";

// Always use current domain for API calls (fixes deployment URL issues)
const BACKEND_URL = typeof window !== 'undefined' ? window.location.origin : '';
export const API = `${BACKEND_URL}/api`;

// Auth context
import { createContext, useContext } from "react";
import { ThemeProvider } from "./context/ThemeContext";

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem("token"));

  const checkAuth = async () => {
    try {
      const response = await fetch(`${API}/auth/me`, {
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      
      // Clone response to avoid "body stream already read" errors
      let text = "";
      try {
        text = await response.text();
      } catch (readError) {
        console.error("Failed to read auth response:", readError);
        setUser(null);
        localStorage.removeItem("token");
        return;
      }
      
      if (response.ok && text) {
        try {
          const userData = JSON.parse(text);
          setUser(userData);
        } catch {
          setUser(null);
          localStorage.removeItem("token");
        }
      } else {
        setUser(null);
        localStorage.removeItem("token");
      }
    } catch (error) {
      console.error("Auth check error:", error);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    checkAuth();
  }, []);

  const login = async (email, password) => {
    try {
      const response = await fetch(`${API}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email, password })
      });
      
      // Clone response first to avoid "body stream already read" errors from browser extensions
      let text;
      try {
        text = await response.clone().text();
      } catch (cloneError) {
        try {
          text = await response.text();
        } catch (textError) {
          console.error("Failed to read login response:", textError);
          throw new Error("Login failed - please try again");
        }
      }
      
      let data;
      try {
        data = JSON.parse(text);
      } catch {
        throw new Error("Login failed - invalid response");
      }
      
      if (!response.ok) {
        throw new Error(data.detail || "Login failed");
      }
      
      setUser(data.user);
      setToken(data.access_token);
      localStorage.setItem("token", data.access_token);
      return data;
    } catch (error) {
      if (error.message && error.message.includes("body stream")) {
        throw new Error("Login may have succeeded - please refresh the page");
      }
      throw error;
    }
  };

  const register = async (formData) => {
    try {
      const response = await fetch(`${API}/auth/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(formData)
      });
      
      // Clone response first to avoid "body stream already read" errors from browser extensions
      let text;
      try {
        text = await response.clone().text();
      } catch (cloneError) {
        // Fallback: try original response
        try {
          text = await response.text();
        } catch (textError) {
          console.error("Failed to read response body:", textError);
          throw new Error("Registration failed - please try again");
        }
      }
      
      let data;
      try {
        data = JSON.parse(text);
      } catch {
        console.error("Invalid JSON response:", text);
        throw new Error("Registration failed - invalid response");
      }
      
      if (!response.ok) {
        throw new Error(data.detail || "Registration failed");
      }
      
      setUser(data.user);
      setToken(data.access_token);
      localStorage.setItem("token", data.access_token);
      return data;
    } catch (error) {
      // Handle "body stream already read" specifically
      if (error.message && error.message.includes("body stream")) {
        throw new Error("Registration may have succeeded - please try logging in");
      }
      throw error;
    }
  };

  const logout = async () => {
    await fetch(`${API}/auth/logout`, {
      method: "POST",
      credentials: "include"
    });
    setUser(null);
    setToken(null);
    localStorage.removeItem("token");
  };

  const processOAuthSession = async (sessionId) => {
    const response = await fetch(`${API}/auth/session`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ session_id: sessionId })
    });
    if (!response.ok) {
      throw new Error("OAuth session processing failed");
    }
    const data = await response.json();
    setUser(data.user);
    return data;
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, login, register, logout, processOAuthSession, token, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected route wrapper
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!loading) {
      if (!user) {
        navigate("/login", { state: { from: location.pathname } });
      } else if (allowedRoles && !allowedRoles.includes(user.role)) {
        // Redirect based on role
        if (user.role === "admin") navigate("/admin");
        else if (user.role === "merchant") navigate("/merchant");
        else navigate("/dashboard");
      }
    }
  }, [user, loading, navigate, allowedRoles, location]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user) return null;
  if (allowedRoles && !allowedRoles.includes(user.role)) return null;

  return children;
};

// App router
const AppRouter = () => {
  const location = useLocation();
  
  // Check for OAuth callback - detect session_id in URL fragment
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/crm" element={<CRMLandingPage />} />
      <Route path="/commerce" element={<CommerceLanding />} />
      <Route path="/commerce/services" element={<CommerceAllServices />} />
      <Route path="/commerce/dropshipping" element={<CommerceServicePage serviceKey="dropshipping" title="Dropshipping Consultancy" intro="End-to-end dropshipping planning, supplier evaluation and launch support." />} />
      <Route path="/commerce/shopify" element={<CommerceServicePage serviceKey="shopify" title="Shopify & E-commerce Development" intro="Conversion-focused Shopify and WooCommerce stores." />} />
      <Route path="/commerce/automation" element={<CommerceServicePage serviceKey="automation" title="AI & Business Automation" intro="AI-powered customer service, workflows and dashboards." />} />
      <Route path="/commerce/pricing" element={<CommercePricing />} />
      <Route path="/commerce/case-studies" element={<CommerceCaseStudies />} />
      <Route path="/commerce/book-consultation" element={<CommerceBookConsultation />} />
      <Route path="/commerce/contact" element={<CommerceContact />} />
      <Route path="/commerce/insights" element={<CommerceInsights />} />
      <Route path="/commerce/insights/:slug" element={<CommerceInsightDetail />} />
      <Route path="/commerce/playbook" element={<CommercePlaybook />} />
      <Route path="/pulse" element={<PulseLanding />} />
      <Route path="/pulse/privacy-policy" element={<PulsePrivacyPolicy />} />
      <Route path="/pulse/terms" element={<PulseTerms />} />
      <Route path="/pulse/delete-account" element={<PulseDeleteAccount />} />
      <Route path="/pulse/unsubscribe" element={<PulseUnsubscribe />} />
      <Route path="/commerce/proposal/:token" element={<CommerceProposalView />} />
      <Route path="/commerce/agreement/:token" element={<CommerceAgreementSign />} />
      <Route path="/commerce/pay/:token" element={<CommercePayInvoice />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/cookies" element={<CookiesPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/consultancy-terms" element={<ConsultancyTermsPage />} />
      <Route path="/refund-policy" element={<RefundPage />} />
      <Route path="/crm/login" element={<CRMLoginPage />} />
      <Route path="/crm/register" element={<CRMRegisterChoicePage />} />
      <Route path="/crm/register/personal" element={<CRMRegisterPersonalPage />} />
      <Route path="/crm/register/business" element={<CRMRegisterBusinessPage />} />
      <Route path="/crm/invite/:token" element={<CRMInviteAcceptPage />} />
      <Route path="/invite/:token" element={<CRMInviteAcceptPage />} /><Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/pricing" element={<MerchantPricingPage />} />
      <Route path="/ai-studio" element={<AIStudioPage />} />
      <Route path="/merchant/payment/success" element={<MerchantPaymentSuccess />} />
      <Route path="/merchant/payment/cancel" element={<MerchantPaymentCancel />} />
      <Route path="/merchant/subscription-manage" element={<MerchantSubscriptionManage />} />
      <Route path="/store" element={<StorefrontPage />} />
      <Route path="/store/:merchantId" element={<MerchantStorePage />} />
      <Route path="/product/:productId" element={<ProductDetailPage />} />
      <Route path="/payment/success" element={<PaymentSuccess />} />
      <Route path="/payment/cancel" element={<PaymentCancel />} />
      <Route path="/agreement/:agreementId" element={<AgreementSignPage />} />
      <Route path="/pos-demo" element={<PosDemoPage />} />
      <Route path="/service-demo" element={<ServiceDemoPage />} />
      <Route path="/crm-demo" element={<Navigate to="/crm" replace />} />
      <Route path="/crm-app/*" element={<CRMAppPage />} />
      <Route path="/crm-onboarding" element={<CRMOnboardingWizard onComplete={() => window.location.href = '/crm-app'} />} />
      <Route path="/book-service/:merchantSlug" element={<PublicBookingPage />} />
      <Route path="/feedback/:merchantSlug/:transactionId" element={<FeedbackPage />} />
      <Route 
        path="/admin/feedback" 
        element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <AdminFeedbackPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/ai-credits" 
        element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <AdminAICreditsPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/merchant/services" 
        element={
          <ProtectedRoute allowedRoles={["merchant", "admin"]}>
            <ServicesPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/merchant/bookings" 
        element={
          <ProtectedRoute allowedRoles={["merchant", "admin"]}>
            <BookingsPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/staff/bookings" 
        element={
          <ProtectedRoute allowedRoles={["merchant", "admin", "staff"]}>
            <StaffBookingsPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/merchant/payouts" 
        element={
          <ProtectedRoute allowedRoles={["merchant", "admin"]}>
            <MerchantPayoutsPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/merchant/documents" 
        element={
          <ProtectedRoute allowedRoles={["merchant"]}>
            <MerchantDocumentsPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/merchant/go-live" 
        element={
          <ProtectedRoute allowedRoles={["merchant"]}>
            <MerchantDashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/merchant/store-settings" 
        element={
          <ProtectedRoute allowedRoles={["merchant"]}>
            <StoreSettingsPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/merchant/billing" 
        element={
          <ProtectedRoute allowedRoles={["merchant", "admin"]}>
            <MerchantBillingPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/merchant/invoices" 
        element={
          <ProtectedRoute allowedRoles={["merchant", "admin"]}>
            <MerchantInvoicesPage />
          </ProtectedRoute>
        } 
      />
      {/* POS Routes */}
      <Route 
        path="/merchant/pos" 
        element={
          <ProtectedRoute allowedRoles={["merchant", "admin"]}>
            <POSTerminalPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/merchant/pos/products" 
        element={
          <ProtectedRoute allowedRoles={["merchant", "admin"]}>
            <POSProductsPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/merchant/pos/terminal" 
        element={
          <ProtectedRoute allowedRoles={["merchant", "admin"]}>
            <POSTerminalPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/merchant/pos/payment-success" 
        element={
          <ProtectedRoute allowedRoles={["merchant", "admin"]}>
            <POSPaymentSuccessPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/merchant/pos/receipt/:transactionId" 
        element={
          <ProtectedRoute allowedRoles={["merchant", "admin"]}>
            <POSReceiptPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/merchant/pos/staff" 
        element={
          <ProtectedRoute allowedRoles={["merchant", "admin"]}>
            <POSStaffPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/merchant/pos/customers" 
        element={
          <ProtectedRoute allowedRoles={["merchant", "admin"]}>
            <POSCustomersPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/merchant/pos/reports" 
        element={
          <ProtectedRoute allowedRoles={["merchant", "admin"]}>
            <POSReportsPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/merchant/pos/clock" 
        element={
          <ProtectedRoute allowedRoles={["merchant", "admin"]}>
            <POSClockPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/merchant/pos/feedback" 
        element={
          <ProtectedRoute allowedRoles={["merchant", "admin"]}>
            <POSFeedbackPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/merchant/ai-social" 
        element={
          <ProtectedRoute allowedRoles={["merchant", "admin"]}>
            <AISocialPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/merchant/crm" 
        element={
          <ProtectedRoute allowedRoles={["merchant", "admin"]}>
            <CRMPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/merchant/pos/ai-assistant" 
        element={
          <ProtectedRoute allowedRoles={["merchant", "admin"]}>
            <POSAIAssistantPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/merchant/pos/rfid-analytics" 
        element={
          <ProtectedRoute allowedRoles={["merchant", "admin"]}>
            <POSRFIDAnalyticsPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/merchant/pos/promotions" 
        element={
          <ProtectedRoute allowedRoles={["merchant", "admin"]}>
            <POSPromotionsPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/merchant/pos/softpos" 
        element={
          <ProtectedRoute allowedRoles={["merchant", "admin"]}>
            <POSSoftPOSPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/merchant/ai-credits" 
        element={
          <ProtectedRoute allowedRoles={["merchant", "admin"]}>
            <MerchantAICreditsPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/dashboard" 
        element={
          <ProtectedRoute allowedRoles={["customer"]}>
            <CustomerDashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/merchant/*" 
        element={
          <ProtectedRoute allowedRoles={["merchant", "admin"]}>
            <MerchantDashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/seo" 
        element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <AdminSEOPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/*" 
        element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <AdminDashboard />
          </ProtectedRoute>
        } 
      />
    </Routes>
  );
};

function App() {
  // Force light mode on initial load if no preference stored
  useEffect(() => {
    const stored = localStorage.getItem('veyronix-dark-mode');
    if (stored === null || stored === 'true') {
      // Default to light mode
      localStorage.setItem('veyronix-dark-mode', 'false');
      document.documentElement.classList.remove('dark');
    }
  }, []);

  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <CRMAuthProvider>
            <AppRouter />
            <Toaster position="top-right" richColors />
          </CRMAuthProvider>
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
